export type PartyAudioScene = 'lobby' | 'game' | 'silent'
export type PartySound = 'join' | 'start' | 'leader' | 'chips' | 'fold' | 'round' | 'hand' | 'winner' | 'error'

export interface PartyAudioState {
  scene: PartyAudioScene
  contextState: AudioContextState | 'uninitialized'
  musicEnabled: boolean
  sfxEnabled: boolean
}

interface VoiceOptions {
  type?: OscillatorType
  attack?: number
  cutoff?: number
  detune?: number
}

interface HarmonyBar {
  bass: number
  chord: number[]
}

const MUSIC_LEVEL = .18
const EFFECTS_LEVEL = .5
const MASTER_LEVEL = .7
const SILENCE = .0001
const MUSIC_LOOKAHEAD_SECONDS = .24
const MUSIC_TICK_MS = 60
const LOBBY_STEPS_PER_MINUTE = 112 * 2
const GAME_STEPS_PER_MINUTE = 92 * 2

// Eight harmonically distinct bars, followed by four arrangement variations,
// make the lobby cue evolve for roughly a minute before it repeats.
const LOBBY_HARMONY: HarmonyBar[] = [
  { bass: 41, chord: [65, 69, 72, 76] }, // Fmaj7
  { bass: 45, chord: [64, 69, 72, 76] }, // Am7/E
  { bass: 46, chord: [65, 70, 74, 77] }, // Bbmaj7
  { bass: 48, chord: [64, 67, 72, 74] }, // Cadd9
  { bass: 41, chord: [65, 69, 72, 76] }, // Fmaj7
  { bass: 43, chord: [67, 70, 74, 77] }, // Gm7
  { bass: 45, chord: [69, 72, 76, 79] }, // Am7
  { bass: 48, chord: [67, 70, 72, 76] }, // C7
]

const LOBBY_MELODY: Array<Array<number | null>> = [
  [69, null, 72, 74, 76, null, 74, 72],
  [69, 72, null, 76, 79, 76, 72, null],
  [70, null, 74, 77, 74, 72, 70, null],
  [67, 72, 74, null, 76, 74, 72, 67],
  [69, null, 72, 76, 77, 76, 72, null],
  [70, 74, null, 77, 79, 77, 74, 70],
  [72, null, 76, 79, 81, 79, 76, 72],
  [74, 72, 70, 67, 69, 70, 72, null],
]

const GAME_HARMONY: HarmonyBar[] = [
  { bass: 38, chord: [62, 65, 69] },
  { bass: 43, chord: [62, 67, 70] },
  { bass: 41, chord: [60, 65, 69] },
  { bass: 45, chord: [61, 64, 69] },
]

let context: AudioContext | null = null
let master: GainNode | null = null
let music: GainNode | null = null
let effects: GainNode | null = null
let compressor: DynamicsCompressorNode | null = null
let noiseSourceBuffer: AudioBuffer | null = null
let audioActive = false
let sfxPreference = false
let scene: PartyAudioScene = 'silent'
let musicPreference: Record<PartyAudioScene, boolean> = {
  lobby: true,
  game: false,
  silent: false,
}
let musicTimer: number | null = null
let musicStep = 0
let nextMusicStepAt = 0
let lifecycleQueue: Promise<unknown> = Promise.resolve()

const midiFrequency = (note: number) => 440 * (2 ** ((note - 69) / 12))

function musicIsEnabled() {
  return audioActive && scene !== 'silent' && musicPreference[scene]
}

function sfxIsEnabled() {
  return audioActive && sfxPreference
}

function audioGraph() {
  if (context && master && music && effects) return { context, master, music, effects }
  context = new AudioContext()
  master = context.createGain()
  music = context.createGain()
  effects = context.createGain()
  compressor = context.createDynamicsCompressor()
  master.gain.value = SILENCE
  music.gain.value = SILENCE
  effects.gain.value = SILENCE
  music.connect(master)
  effects.connect(master)
  compressor.threshold.value = -18
  compressor.knee.value = 16
  compressor.ratio.value = 5
  compressor.attack.value = .006
  compressor.release.value = .22
  master.connect(compressor)
  compressor.connect(context.destination)
  return { context, master, music, effects }
}

function smoothGain(node: GainNode, value: number, timeConstant = .025) {
  if (!context) return
  node.gain.cancelScheduledValues(context.currentTime)
  node.gain.setTargetAtTime(value, context.currentTime, timeConstant)
}

function applyMix() {
  if (!context || !master || !music || !effects) return
  const anyChannelEnabled = musicIsEnabled() || sfxIsEnabled()
  smoothGain(master, anyChannelEnabled ? MASTER_LEVEL : SILENCE)
  smoothGain(music, musicIsEnabled() ? MUSIC_LEVEL : SILENCE, .04)
  smoothGain(effects, sfxIsEnabled() ? EFFECTS_LEVEL : SILENCE)
}

async function resumeAudioGraph() {
  const graph = audioGraph()
  lifecycleQueue = lifecycleQueue.catch(() => undefined).then(async () => {
    if (graph.context.state === 'suspended') await graph.context.resume()
  })
  await lifecycleQueue
  return graph.context.state === 'running'
}

function tone(
  frequency: number,
  start: number,
  duration: number,
  volume: number,
  destination: AudioNode,
  options: VoiceOptions | OscillatorType = {},
) {
  const graph = audioGraph()
  const resolved = typeof options === 'string' ? { type: options } : options
  const oscillator = graph.context.createOscillator()
  const filter = resolved.cutoff ? graph.context.createBiquadFilter() : null
  const gain = graph.context.createGain()
  const attack = Math.min(resolved.attack ?? .018, duration / 3)
  oscillator.type = resolved.type ?? 'triangle'
  oscillator.frequency.setValueAtTime(frequency, start)
  if (resolved.detune) oscillator.detune.setValueAtTime(resolved.detune, start)
  gain.gain.setValueAtTime(SILENCE, start)
  gain.gain.exponentialRampToValueAtTime(Math.max(.0002, volume), start + attack)
  gain.gain.exponentialRampToValueAtTime(SILENCE, start + duration)
  if (filter) {
    filter.type = 'lowpass'
    filter.frequency.setValueAtTime(resolved.cutoff!, start)
    filter.frequency.exponentialRampToValueAtTime(Math.max(120, resolved.cutoff! * .52), start + duration)
    oscillator.connect(filter)
    filter.connect(gain)
  } else {
    oscillator.connect(gain)
  }
  gain.connect(destination)
  oscillator.start(start)
  oscillator.stop(start + duration + .03)
}

function noise(start: number, duration: number, volume: number, destination: AudioNode, highpass = 850) {
  const graph = audioGraph()
  if (!noiseSourceBuffer || noiseSourceBuffer.sampleRate !== graph.context.sampleRate) {
    noiseSourceBuffer = graph.context.createBuffer(1, graph.context.sampleRate, graph.context.sampleRate)
    const samples = noiseSourceBuffer.getChannelData(0)
    for (let index = 0; index < samples.length; index += 1) samples[index] = Math.random() * 2 - 1
  }
  const source = graph.context.createBufferSource()
  const filter = graph.context.createBiquadFilter()
  const gain = graph.context.createGain()
  source.buffer = noiseSourceBuffer
  filter.type = 'highpass'
  filter.frequency.value = highpass
  gain.gain.setValueAtTime(volume, start)
  gain.gain.exponentialRampToValueAtTime(SILENCE, start + duration)
  source.connect(filter)
  filter.connect(gain)
  gain.connect(destination)
  const maxOffset = Math.max(0, noiseSourceBuffer.duration - duration)
  source.start(start, Math.random() * maxOffset, duration)
}

function kick(start: number, volume: number, destination: AudioNode) {
  const graph = audioGraph()
  const oscillator = graph.context.createOscillator()
  const gain = graph.context.createGain()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(118, start)
  oscillator.frequency.exponentialRampToValueAtTime(47, start + .12)
  gain.gain.setValueAtTime(volume, start)
  gain.gain.exponentialRampToValueAtTime(SILENCE, start + .16)
  oscillator.connect(gain)
  gain.connect(destination)
  oscillator.start(start)
  oscillator.stop(start + .18)
}

function chordStab(notes: number[], start: number, volume: number, destination: AudioNode, bright = false) {
  notes.forEach((note, index) => {
    tone(midiFrequency(note), start + index * .007, bright ? .25 : .34, volume, destination, {
      type: index % 2 ? 'triangle' : 'sine',
      attack: .012,
      cutoff: bright ? 2200 : 1450,
      detune: index % 2 ? 3 : -3,
    })
  })
}

function melodyNote(note: number, start: number, volume: number, destination: AudioNode, variation: number) {
  tone(midiFrequency(note), start, .18, volume, destination, { type: 'triangle', cutoff: 2600, attack: .012 })
  if (variation === 3) {
    tone(midiFrequency(note + 12), start + .008, .11, volume * .28, destination, { type: 'sine', attack: .008 })
  }
}

function scheduleLobbyMusicStep(start: number, step: number) {
  const graph = audioGraph()
  const stepInBar = step % 8
  const barIndex = Math.floor(step / 8) % LOBBY_HARMONY.length
  const variation = Math.floor(step / 64) % 4
  const bar = LOBBY_HARMONY[barIndex]
  const melody = LOBBY_MELODY[barIndex][stepInBar]
  const isBreakdown = variation === 2 && barIndex < 2
  const isBuild = variation === 3

  if (stepInBar === 0 || stepInBar === 3 || stepInBar === 6 || (isBuild && stepInBar === 7)) {
    const interval = stepInBar === 3 ? 7 : stepInBar === 6 ? 12 : stepInBar === 7 ? 5 : 0
    tone(midiFrequency(bar.bass + interval), start, .2, isBreakdown ? .12 : .2, graph.music, {
      type: 'square',
      cutoff: 520,
      attack: .008,
    })
  }

  if ((!isBreakdown && (stepInBar === 1 || stepInBar === 4)) || (isBreakdown && stepInBar === 0)) {
    chordStab(bar.chord, start, isBreakdown ? .042 : .052, graph.music, stepInBar === 4)
  }

  if (melody != null && !(variation === 0 && barIndex < 2 && stepInBar % 2 === 1)) {
    const shifted = variation === 1 && (stepInBar === 3 || stepInBar === 6) ? melody + 12 : melody
    melodyNote(shifted, start, isBreakdown ? .055 : .082, graph.music, variation)
  }

  if (!isBreakdown) {
    if (stepInBar === 0 || stepInBar === 4 || (isBuild && stepInBar === 7)) kick(start, .24, graph.music)
    if (stepInBar === 2 || stepInBar === 6) noise(start, .11, .085, graph.music, 1150)
    noise(start, stepInBar % 2 ? .035 : .024, stepInBar % 2 ? .032 : .018, graph.music, 4800)
  } else if (stepInBar % 2 === 0) {
    noise(start, .03, .017, graph.music, 5600)
  }

  if (isBuild && barIndex === 7 && stepInBar >= 5) {
    noise(start, .07, .045 + (stepInBar - 5) * .015, graph.music, 1700)
  }
}

function scheduleGameMusicStep(start: number, step: number) {
  const graph = audioGraph()
  const stepInBar = step % 8
  const bar = GAME_HARMONY[Math.floor(step / 8) % GAME_HARMONY.length]
  if (stepInBar === 0 || stepInBar === 4) {
    tone(midiFrequency(bar.bass), start, .26, .16, graph.music, { type: 'triangle', cutoff: 440, attack: .014 })
  }
  if (stepInBar === 1 || stepInBar === 5) chordStab(bar.chord, start, .035, graph.music)
  if (stepInBar === 2 || stepInBar === 6) noise(start, .05, .027, graph.music, 3600)
}

function stopMusicClock() {
  if (musicTimer != null) window.clearInterval(musicTimer)
  musicTimer = null
  musicStep = 0
  nextMusicStepAt = 0
}

function scheduleMusicStep(start: number) {
  if (!musicIsEnabled()) return
  const graph = audioGraph()
  if (graph.context.state !== 'running') return
  if (scene === 'lobby') scheduleLobbyMusicStep(start, musicStep)
  if (scene === 'game') scheduleGameMusicStep(start, musicStep)
  musicStep = (musicStep + 1) % 256
}

function pumpMusicClock() {
  if (!musicIsEnabled()) return
  const graph = audioGraph()
  if (graph.context.state !== 'running') return
  if (nextMusicStepAt < graph.context.currentTime) nextMusicStepAt = graph.context.currentTime + .06
  const stepsPerMinute = scene === 'lobby' ? LOBBY_STEPS_PER_MINUTE : GAME_STEPS_PER_MINUTE
  const secondsPerStep = 60 / stepsPerMinute
  while (nextMusicStepAt < graph.context.currentTime + MUSIC_LOOKAHEAD_SECONDS) {
    scheduleMusicStep(nextMusicStepAt)
    nextMusicStepAt += secondsPerStep
  }
}

function startMusicClock() {
  stopMusicClock()
  if (!musicIsEnabled()) return
  pumpMusicClock()
  musicTimer = window.setInterval(pumpMusicClock, MUSIC_TICK_MS)
}

export function getPartyAudioState(): PartyAudioState {
  return {
    scene,
    contextState: context?.state ?? 'uninitialized',
    musicEnabled: musicIsEnabled(),
    sfxEnabled: sfxIsEnabled(),
  }
}

export async function enablePartyAudio(nextScene: PartyAudioScene = 'lobby') {
  const running = await resumeAudioGraph()
  audioActive = running
  scene = nextScene
  sfxPreference = true
  applyMix()
  startMusicClock()
}

export function disablePartyAudio() {
  audioActive = false
  sfxPreference = false
  musicPreference = { lobby: true, game: false, silent: false }
  stopMusicClock()
  applyMix()
}

export function isPartyAudioEnabled() {
  return (musicIsEnabled() || sfxIsEnabled()) && context?.state === 'running'
}

export function isPartyMusicEnabled() {
  return musicIsEnabled()
}

export function isPartySfxEnabled() {
  return sfxIsEnabled()
}

export async function setPartyMusicEnabled(nextEnabled: boolean) {
  if (scene === 'silent') return false
  const previousPreference = musicPreference[scene]
  const previousAudioActive = audioActive
  musicPreference[scene] = nextEnabled
  try {
    if (nextEnabled) {
      const running = await resumeAudioGraph()
      if (!running) {
        musicPreference[scene] = previousPreference
        audioActive = previousAudioActive
      } else {
        audioActive = true
      }
    }
  } catch (reason) {
    musicPreference[scene] = previousPreference
    audioActive = previousAudioActive
    applyMix()
    if (musicIsEnabled()) startMusicClock()
    else stopMusicClock()
    throw reason
  }
  applyMix()
  if (musicIsEnabled()) startMusicClock()
  else stopMusicClock()
  return musicIsEnabled()
}

export async function setPartySfxEnabled(nextEnabled: boolean) {
  const previousPreference = sfxPreference
  const previousAudioActive = audioActive
  sfxPreference = nextEnabled
  try {
    if (nextEnabled) {
      const running = await resumeAudioGraph()
      if (!running) {
        sfxPreference = previousPreference
        audioActive = previousAudioActive
      } else {
        audioActive = true
      }
    }
  } catch (reason) {
    sfxPreference = previousPreference
    audioActive = previousAudioActive
    applyMix()
    throw reason
  }
  applyMix()
  return sfxIsEnabled()
}

export function setPartyAudioScene(nextScene: PartyAudioScene) {
  if (nextScene === scene) return getPartyAudioState()
  scene = nextScene
  applyMix()
  startMusicClock()
  return getPartyAudioState()
}

export async function restorePartyAudio() {
  if (!audioActive || !context) return false
  try {
    const running = await resumeAudioGraph()
    applyMix()
    if (running && musicIsEnabled()) startMusicClock()
    return running
  } catch {
    return false
  }
}

export function suspendPartyAudio() {
  stopMusicClock()
  const currentContext = context
  if (currentContext?.state === 'running') {
    lifecycleQueue = lifecycleQueue.catch(() => undefined).then(() => currentContext.suspend()).catch(() => undefined)
  }
}

export function playPartySound(sound: PartySound, amount = 0) {
  if (!sfxIsEnabled()) return
  const graph = audioGraph()
  if (graph.context.state !== 'running') return
  const now = graph.context.currentTime + .018
  const playNotes = (notes: number[], spacing: number, duration = .16, volume = .22, type: OscillatorType = 'triangle') => {
    notes.forEach((note, index) => tone(midiFrequency(note), now + index * spacing, duration, volume, graph.effects, type))
  }

  if (sound === 'join') playNotes([67, 72, 76], .07, .16, .2)
  if (sound === 'start') playNotes([55, 60, 64, 67, 72], .11, .3, .24, 'square')
  if (sound === 'leader') {
    playNotes([60, 67, 72], .065, .24, .2, 'triangle')
    noise(now + .14, .08, .055, graph.effects, 2200)
  }
  if (sound === 'round') {
    tone(midiFrequency(48), now, .42, .28, graph.effects, 'sawtooth')
    playNotes([60, 67, 72], .035, .32, .18)
  }
  if (sound === 'hand') playNotes([62, 65, 69, 74], .075, .2, .2)
  if (sound === 'winner') {
    noise(now, .45, .12, graph.effects, 1800)
    playNotes([60, 64, 67, 72, 76], .085, .42, .25, 'square')
  }
  if (sound === 'fold') noise(now, .24, .18, graph.effects, 420)
  if (sound === 'error') playNotes([44, 41], .1, .18, .16, 'square')
  if (sound === 'chips') {
    const weight = Math.min(1, Math.max(.25, Math.log10(Math.max(2, amount)) / 3))
    const hits = amount >= 100 ? 4 : amount >= 25 ? 3 : 2
    for (let index = 0; index < hits; index += 1) {
      const at = now + index * .045
      tone(170 + Math.random() * 80, at, .075, .12 + weight * .09, graph.effects, 'square')
      noise(at, .05, .06 + weight * .05, graph.effects, 1300)
    }
  }
}
